<template>
  <div>
    <component
      :is="loading ? PrmIconField : 'div'"
    >
      <PrmInputNumber
        :style="inputProps?.style"
        :class="{
          ...normalizeClass(inputProps?.class),
          'p-invalid': invalid || !!errorMessage,
        }"
        :model-value="modelValue"
        :disabled="loading || disabled"
        :min="minValue"
        :max="maxValue"
        :min-fraction-digits="minFractionDigits"
        :max-fraction-digits="maxFractionDigits"
        allow-empty
        :use-grouping="false"
        :placeholder="placeholder"
        :suffix="formattedSuffix"
        :pt="{
          // we override idiotic primevue styles
          // but it's unsafe to change it in whole project, so we do it for our wrapper
          root: {
            style: {
              display: 'flex',
            },
          },
          input: {
            root: {
              'data-qa': 'input-number',
              style: {
                width: '100%',
                paddingRight: loading ? '40px' : undefined,
              },
            },
          },
        }"
        @update:model-value="onUpdate"
      />
      <PrmInputIcon
        v-if="loading"
        class="pi pi-spin pi-spinner"
      />
    </component>
    <span
      v-if="!!errorMessage"
      class="input-wrapper-error"
    >
      {{ errorMessage }}
    </span>
  </div>
</template>

<script lang="ts" setup generic="ALLOW_NULLISH extends boolean = false">
import PrmIconField from '@NitrexGroup/primevue/iconfield';
import PrmInputIcon from '@NitrexGroup/primevue/inputicon';
import PrmInputNumber from '@NitrexGroup/primevue/inputnumber';
import {
  computed,
} from 'vue';

import {
  HTMLProperties, normalizeClass,
} from '../../types/props';
import {
  isNullish,
} from '../../utils/common';

const props = defineProps<{
  modelValue?: number | null,
  loading?: boolean,
  disabled?: boolean,
  invalid?: boolean,
  errorMessage?: string,
  minValue?: number,
  maxValue?: number,
  minFractionDigits?: number,
  maxFractionDigits?: number,
  /**
   * Allows emitted value to be 'undefined' or 'null'
   */
  allowNullish?: ALLOW_NULLISH,
  /* Freetext suffix. For units use unitOfMeasurement */
  suffix?: string,
  placeholder?: string,
  inputProps?: HTMLProperties,
}>();

const formattedSuffix = computed(() => {
  const actualSuffix = props.suffix?.trim();
  if (!actualSuffix) {
    return undefined;
  }
  // prefix suffix with one whitespace.
  // Background: https://www.bipm.org/en/publications/si-brochure/ Section 5.4.3
  return ' ' + actualSuffix;
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: ALLOW_NULLISH extends true ? number | null | undefined : number): void,
}>();

/*
 * Vue's boolean casting only applies when a prop is typed as literally `boolean`.
 * Because `allowNullish` is typed as the generic parameter `ALLOW_NULLISH`, Vue's
 * compiler cannot resolve it to `boolean` at compile time and therefore skips boolean
 * casting. As a result, a bare attribute (e.g. `allow-nullish` without a binding)
 * follows the HTML spec and arrives as an empty string `""` instead of `true`.
 *
 * The computed handles all three valid cases:
 *   - prop not set            → undefined  → false
 *   - :allow-nullish="true"   → true       → true
 *   - allow-nullish (bare)    → ""         → true
 *
 * Example of the bare-attribute usage that triggers the empty-string case:
 *
 * <InputNumber
 *   :model-value="data.limitMin"
 *   allow-nullish
 * />
 */
const allowNullish = computed(() => props.allowNullish === true || (props.allowNullish as unknown as string) === '');

function onUpdate(newValue: number | null) {
  if (isNullish(newValue) && !allowNullish.value) {
    return;
  }
  emit('update:modelValue', newValue as any);
}
</script>
